import { Flex, Stack, Switch } from '@mantine/core'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { memo, useCallback, useEffect, useState } from 'react'
import {
	deleteChangelog,
	getListChangelog,
	getOneChangelog,
	publishChangelog,
	unpublishChangelog,
} from 'src/apis/changelogAPI'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { ChangelogData } from 'src/interfaces'
import { filterNormalIlike } from 'src/utils/filter'
import { formatDateTime, size } from 'src/variable/constant'
import ModalCreateChangelog from './ModalCreateChangelog'

interface ChangelogPageListProps {
	columns: MRT_ColumnDef[]
	fields: {
		value: string
		label: string
	}[]
	[key: string]: any
}

function ChangelogPageList({ columns, fields }: ChangelogPageListProps) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [data, setData] = useState<ChangelogData[]>([])
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [value, setValue] = useState({
		field: fields[0].value,
		value: null,
	})
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)
	const [open, setOpen] = useState({
		open: false,
		data: null as ChangelogData,
	})

	const handleGetData = useCallback(async () => {
		let arr: ChangelogData[] = []
		let count = 0
		let filter = {}
		setLoading(true)
		try {
			filter = filterNormalIlike(value)
			const res = await getListChangelog({
				fields: ['$all'],
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				order: [{ created_at: 'desc' }],
				where: { ...filter },
			})
			arr = res?.data?.results?.objects?.rows?.map((item, index) => ({
				...item,
				no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
				created_at: dayjs(item?.created_at).format(formatDateTime),
				updated_at: dayjs(item?.updated_at).format(formatDateTime),
				published_at: item?.published_at
					? dayjs(item?.published_at).format(formatDateTime)
					: '',
			}))
			count = res?.data?.results?.objects?.count || 0
		} catch (error) {
			openError(error)
		} finally {
			setData(arr)
			setTotal(count)
			setTimeout(() => {
				setLoading(false)
			}, 500)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination), JSON.stringify(value)])

	const handleSetValue = (e, key) => {
		if (key === 'field' && value.value === '') {
			setValue((pre) => ({ ...pre, [key]: e, value: null }))
		} else {
			setValue((pre) => ({ ...pre, [key]: e }))
		}
	}

	const handleChangePage = useCallback((nextPage: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: nextPage,
		}))
	}, [])

	const handleEdit = useCallback(
		async (row) => {
			const id = row?.original?.id
			if (!id) {
				return
			}
			openLoadingContext()
			try {
				const res = await getOneChangelog(id, { fields: ['$all'] })
				const detail = res?.data?.results?.object || row?.original
				setOpen({ open: true, data: detail })
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	const handleDeleteChangelog = useCallback(
		async (id: string) => {
			openLoadingContext()
			try {
				const res = await deleteChangelog(id)
				if (res?.data?.code === 200) {
					await handleGetData()
					openModal({
						type: 'success',
						label: 'You have successfully deleted this changelog !',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleGetData],
	)

	const handleDelete = useCallback(
		(row: any) => {
			openModal({
				type: 'confirm',
				label: <div>Do you want to delete this changelog ?</div>,
				handleConfirm: () => {
					handleDeleteChangelog(row?.original?.id)
				},
			})
		},
		[handleDeleteChangelog, openModal],
	)

	const handleTogglePublish = useCallback(
		async (row: ChangelogData) => {
			const id = row?.id
			if (!id) {
				return
			}
			openLoadingContext()
			try {
				const res = row?.is_published
					? await unpublishChangelog(id)
					: await publishChangelog(id)
				if (res?.data?.code === 200) {
					await handleGetData()
					openModal({
						type: 'success',
						label: row?.is_published
							? 'Changelog has been unpublished successfully!'
							: 'Changelog has been published successfully!',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleGetData],
	)

	const handleConfirmTogglePublish = useCallback(
		(row: ChangelogData) => {
			openModal({
				type: 'confirm',
				label: (
					<div>
						Do you want to {row?.is_published ? 'unpublish' : 'publish'} this
						 changelog ?
					</div>
				),
				handleConfirm: () => {
					handleTogglePublish(row)
				},
			})
		},
		[handleTogglePublish, openModal],
	)

	const statusCol = {
		accessorKey: 'is_published',
		header: 'Published',
		maxSize: size.no,
		Cell: ({ cell, row }) => (
			<Switch
				onLabel="Yes"
				offLabel="No"
				radius="sm"
				size="xl"
				sx={{
					'.mantine-Switch-body': {
						'.mantine-Switch-track': {
							cursor: 'pointer',
							width: 100,
						},
					},
					'.mantine-Switch-trackLabel': {
						fontSize: '0.875rem',
					},
				}}
				checked={Boolean(cell.getValue<boolean>())}
				onChange={() => handleConfirmTogglePublish(row.original as ChangelogData)}
			/>
		),
	} as MRT_ColumnDef

	const columnsNew = [
		...columns.slice(0, 4),
		statusCol,
		...columns.slice(4),
	]

	useEffect(() => {
		handleGetData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination)])

	useEffect(() => {
		let timer
		if (value.value !== null) {
			timer = setTimeout(() => {
				if (pagination.pageIndex === 1) {
					handleGetData()
				} else handleChangePage(1)
			}, 500)
		}
		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(value)])

	return (
		<Stack sx={{ width: '100%' }}>
			<Flex gap={8} justify="space-between" sx={{ flexWrap: 'wrap' }}>
				<Flex gap={8} sx={{ flexWrap: 'wrap' }}>
					<CustomSelect
						value={value.field}
						data={fields}
						onChange={(e) => {
							handleSetValue(e, 'field')
						}}
						sx={{
							input: {
								color: 'black !important',
								fontWeight: 600,
							},
						}}
					/>
					<CustomClearInput
						value={value.value}
						onChange={(e) => handleSetValue(e, 'value')}
						placeholder={'Search'}
					/>
				</Flex>

				<CustomButton
					variant="outlined"
					sx={{ height: '36px', whiteSpace: 'nowrap', minWidth: '240px' }}
					onClick={() => setOpen({ open: true, data: null })}
				>
					Create
				</CustomButton>
			</Flex>

			<TableComponent
				columns={columnsNew}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				// searchQueryValue={typeof value.value === 'string' ? value.value : undefined}
				// onSearchQueryInit={handleSearchQueryInit}
				// filterQueryValues={{
				// 	search_field: value.field,
				// }}
				// onFilterQueryInit={handleFilterQueryInit}
				handleEdit={handleEdit}
				handleDelete={handleDelete}
			/>

			{open.open && (
				<ModalCreateChangelog
					open={true}
					data={open.data}
					onClose={() => setOpen({ open: false, data: null })}
					handleGetData={handleGetData}
					pagination={pagination}
					handleChangePage={handleChangePage}
				/>
			)}
		</Stack>
	)
}

export default memo(ChangelogPageList)
